import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import venueService from '../services/VenueService';
import { useAuth } from '../contexts/AuthContext';
import {
  Card,
  Button,
  SearchInput,
  Select,
  CustomDropdown,
  Table,
  Modal,
  Input,
  Alert
} from '../components/ui';

/**
 * VenuesPage component for managing therapy venues/rooms
 * @returns {React.ReactElement} VenuesPage component
 */
const VenuesPage = () => {
  // State for venues data
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for search and filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');

  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [venuesPerPage] = useState(10);

  // State for creating/editing venues
  const [showVenueModal, setShowVenueModal] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'therapy',
    location: '',
    capacity: 1,
    description: '',
    amenities: '',
    isActive: true
  });
  const [formErrors, setFormErrors] = useState({});

  const { hasPermission } = useAuth();
  const canManageVenues = hasPermission('venues', 'write');

  // Fetch venues on component mount
  useEffect(() => {
    fetchVenues();
  }, []);

  // Function to fetch venues
  const fetchVenues = async () => {
    setLoading(true);
    try {
      const data = venueService.getAllVenues();
      setVenues(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching venues:', err);
      setError('获取场地数据失败');
    } finally {
      setLoading(false);
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // Handle filter change
  const handleFilterChange = (e) => {
    setFilter(e.target.value);
    setCurrentPage(1);
  };

  // Filter and search venues
  const filteredVenues = useMemo(() => {
    return venues.filter(venue => {
      const matchesSearch =
        venue.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        venue.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        venue.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        venue.amenities?.toLowerCase().includes(searchTerm.toLowerCase());

      if (filter === 'all') return matchesSearch;
      if (filter === 'active') return matchesSearch && venue.isActive;
      if (filter === 'inactive') return matchesSearch && !venue.isActive;
      if (filter === 'therapy') return matchesSearch && venue.type === 'therapy';
      if (filter === 'consultation') return matchesSearch && venue.type === 'consultation';
      if (filter === 'treatment') return matchesSearch && venue.type === 'treatment';
      return matchesSearch;
    });
  }, [venues, searchTerm, filter]);

  // Get current venues for pagination
  const indexOfLastVenue = currentPage * venuesPerPage;
  const indexOfFirstVenue = indexOfLastVenue - venuesPerPage;
  const currentVenues = filteredVenues.slice(indexOfFirstVenue, indexOfLastVenue);
  const totalPages = Math.ceil(filteredVenues.length / venuesPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Handle modal open for new venue
  const handleAddNewVenue = () => {
    setSelectedVenue(null);
    setFormData({
      name: '',
      type: 'therapy',
      location: '',
      capacity: 1,
      description: '',
      amenities: '',
      isActive: true
    });
    setFormErrors({});
    setShowVenueModal(true);
  };

  // Handle modal open for edit venue
  const handleEditVenue = (venue) => {
    setSelectedVenue(venue);
    setFormData({
      name: venue.name || '',
      type: venue.type || 'therapy',
      location: venue.location || '',
      capacity: venue.capacity || 1,
      description: venue.description || '',
      amenities: venue.amenities || '',
      isActive: venue.isActive !== undefined ? venue.isActive : true
    });
    setFormErrors({});
    setShowVenueModal(true);
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = '请输入场地名称';
    }

    if (!formData.location.trim()) {
      errors.location = '请输入场地位置';
    }

    if (formData.capacity < 1 || formData.capacity > 50) {
      errors.capacity = '容纳人数必须在1-50之间';
    }

    return errors;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      if (selectedVenue) {
        // Update existing venue
        venueService.updateVenue(selectedVenue.id, formData);
      } else {
        // Create new venue
        venueService.createVenue(formData);
      }

      // Refresh venue list
      fetchVenues();
      setShowVenueModal(false);
    } catch (err) {
      console.error('Error saving venue:', err);
      setError('保存场地信息失败');
    }
  };

  // Handle venue delete
  const handleDeleteVenue = (venueId) => {
    if (window.confirm('确定要删除此场地吗？此操作无法撤销。')) {
      try {
        venueService.deleteVenue(venueId);
        fetchVenues();
      } catch (err) {
        console.error('Error deleting venue:', err);
        setError('删除场地失败');
      }
    }
  };

  // Handle toggle venue status
  const handleToggleStatus = (venue) => {
    try {
      venueService.updateVenue(venue.id, { isActive: !venue.isActive });
      fetchVenues();
    } catch (err) {
      console.error('Error updating venue status:', err);
      setError('更新场地状态失败');
    }
  };

  // Table columns configuration
  const columns = [
    {
      key: 'venue',
      title: '场地信息',
      render: (_, venue) => (
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center text-white font-semibold shadow-lg">
              {venue.name ? venue.name.charAt(0) : '?'}
            </div>
          </div>
          <div>
            <div className="text-sm font-medium text-gray-900">
              {venue.name || '未命名场地'}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {venue.location || '未指定位置'}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'type',
      title: '场地类型',
      render: (_, venue) => {
        const typeMap = {
          therapy: '理疗室',
          consultation: '咨询室',
          treatment: '治疗室',
          waiting: '等候区',
          other: '其他'
        };
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {typeMap[venue.type] || venue.type || '未指定'}
          </span>
        );
      }
    },
    {
      key: 'capacity',
      title: '容纳人数',
      render: (_, venue) => (
        <div className="text-sm text-gray-900">
          {venue.capacity || 1} 人
        </div>
      )
    },
    {
      key: 'description',
      title: '场地描述',
      render: (_, venue) => (
        <div className="max-w-xs">
          {venue.description ? (
            <p className="text-sm text-gray-600 line-clamp-2">
              {venue.description}
            </p>
          ) : (
            <span className="text-sm text-gray-400">无描述</span>
          )}
        </div>
      )
    },
    {
      key: 'status',
      title: '状态',
      render: (_, venue) => (
        venue.isActive ? (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
            可用
          </span>
        ) : (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            不可用
          </span>
        )
      )
    },
    {
      key: 'amenities',
      title: '配套设施',
      render: (_, venue) => (
        <div className="max-w-xs">
          {venue.amenities ? (
            <p className="text-sm text-gray-600 line-clamp-1">
              {venue.amenities}
            </p>
          ) : (
            <span className="text-sm text-gray-400">无设施信息</span>
          )}
        </div>
      )
    },
    {
      key: 'actions',
      title: '操作',
      render: (_, venue) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleToggleStatus(venue)}
            className={`text-sm font-medium transition-colors duration-200 ${venue.isActive
                ? 'text-yellow-600 hover:text-yellow-700'
                : 'text-emerald-600 hover:text-emerald-700'
              }`}
          >
            {venue.isActive ? '禁用' : '启用'}
          </button>
          {canManageVenues && (
            <>
              <span className="text-gray-300">|</span>
              <button
                onClick={() => handleEditVenue(venue)}
                className="text-gray-600 hover:text-gray-700 text-sm font-medium transition-colors duration-200"
              >
                编辑
              </button>
              <span className="text-gray-300">|</span>
              <button
                onClick={() => handleDeleteVenue(venue.id)}
                className="text-red-600 hover:text-red-700 text-sm font-medium transition-colors duration-200"
              >
                删除
              </button>
            </>
          )}
        </div>
      ),
      cellClassName: 'text-right'
    }
  ];

  // Filter options
  const filterOptions = [
    { value: 'all', label: '全部场地' },
    { value: 'active', label: '可用场地' },
    { value: 'inactive', label: '不可用场地' },
    { value: 'therapy', label: '理疗室' },
    { value: 'consultation', label: '咨询室' },
    { value: 'treatment', label: '治疗室' }
  ];

  // Venue type options
  const venueTypeOptions = [
    { value: 'therapy', label: '理疗室' },
    { value: 'consultation', label: '咨询室' },
    { value: 'treatment', label: '治疗室' },
    { value: 'waiting', label: '等候区' },
    { value: 'other', label: '其他' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50/30 to-blue-50/30 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              调理中心管理
            </h1>
            <p className="text-gray-600">
              管理调理中心和房间，配置设施和可用性
            </p>
          </div>
          {canManageVenues && (
            <div className="mt-4 sm:mt-0">
              <Button
                onClick={handleAddNewVenue}
              >
                <svg className="mr-2 -ml-1 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                添加场地
              </Button>
            </div>
          )}
        </div>

        {/* Search and Filter */}
        <Card variant="simple" className="border-emerald-200/50 bg-white/70 backdrop-blur-sm">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <SearchInput
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="搜索场地名称、位置或描述..."
                className="w-full"
              />
            </div>
            <div>
              <CustomDropdown
                options={filterOptions}
                value={filter}
                onChange={handleFilterChange}
                placeholder="选择筛选条件"
              />
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
            <span>共找到 {filteredVenues.length} 个场地</span>
            {searchTerm && (
              <span>搜索: "{searchTerm}"</span>
            )}
          </div>
        </Card>

        {/* Error Display */}
        {error && (
          <Alert variant="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Venues Table */}
        <Table
          columns={columns}
          data={currentVenues}
          loading={loading}
          variant="simple"
          className="border-emerald-200/50 bg-white/70 backdrop-blur-sm"
          emptyMessage="没有找到场地"
          emptyIcon={
            <svg className="h-16 w-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          }
          emptyAction={
            canManageVenues ? (
              <Button onClick={handleAddNewVenue} className="mt-4">
                <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                添加场地
              </Button>
            ) : null
          }
        />

        {/* Pagination */}
        {filteredVenues.length > venuesPerPage && (
          <Card variant="simple" className="border-emerald-200/50 bg-white/70 backdrop-blur-sm">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                显示 <span className="font-medium">{indexOfFirstVenue + 1}</span> 至{' '}
                <span className="font-medium">
                  {Math.min(indexOfLastVenue, filteredVenues.length)}
                </span>{' '}
                条，共 <span className="font-medium">{filteredVenues.length}</span> 条结果
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="small"
                  onClick={() => paginate(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </Button>

                <div className="flex items-center space-x-1">
                  {[...Array(totalPages)].map((_, index) => {
                    const pageNumber = index + 1;
                    const shouldDisplay =
                      pageNumber === 1 ||
                      pageNumber === totalPages ||
                      (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1);

                    if (!shouldDisplay) {
                      if (pageNumber === 2 || pageNumber === totalPages - 1) {
                        return (
                          <span key={`ellipsis-${pageNumber}`} className="px-2 text-gray-500">
                            ...
                          </span>
                        );
                      }
                      return null;
                    }

                    return (
                      <Button
                        key={pageNumber}
                        variant={currentPage === pageNumber ? "primary" : "ghost"}
                        size="small"
                        onClick={() => paginate(pageNumber)}
                        className="min-w-[36px]"
                      >
                        {pageNumber}
                      </Button>
                    );
                  })}
                </div>

                <Button
                  variant="ghost"
                  size="small"
                  onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Button>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Add/Edit Venue Modal */}
      <Modal
        isOpen={showVenueModal}
        onClose={() => setShowVenueModal(false)}
        title={selectedVenue ? '编辑场地信息' : '添加新场地'}
        size="large"
        footer={
          <div className="flex space-x-3">
            <Button
              variant="secondary"
              onClick={() => setShowVenueModal(false)}
            >
              取消
            </Button>
            <Button
              type="submit"
              form="venue-form"
            >
              {selectedVenue ? '保存修改' : '添加场地'}
            </Button>
          </div>
        }
      >
        <form id="venue-form" onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="场地名称"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              error={formErrors.name}
              required
              placeholder="如：1号诊室、足疗室等"
            />

            <CustomDropdown
              label="场地类型"
              options={venueTypeOptions}
              value={formData.type}
              onChange={handleInputChange}
              name="type"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="场地位置"
              name="location"
              value={formData.location}
              onChange={handleInputChange}
              error={formErrors.location}
              required
              placeholder="如：一楼东侧、二楼西侧等"
            />

            <Input
              label="容纳人数"
              name="capacity"
              type="number"
              min="1"
              max="50"
              value={formData.capacity}
              onChange={handleInputChange}
              error={formErrors.capacity}
              required
              placeholder="可容纳的人数"
            />
          </div>

          <Input
            label="场地描述"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="场地的简要描述"
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              配套设施
            </label>
            <textarea
              name="amenities"
              rows={4}
              value={formData.amenities}
              onChange={handleInputChange}
              className="w-full px-4 py-3 text-sm border border-gray-300 bg-white text-gray-900 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200"
              placeholder="如：按摩床、针灸设备、艾灸仪等（每行一个设施）"
            />
          </div>

          <div className="flex items-center">
            <input
              id="isActive"
              name="isActive"
              type="checkbox"
              checked={formData.isActive}
              onChange={handleInputChange}
              className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
            />
            <label htmlFor="isActive" className="ml-3 block text-sm font-medium text-gray-700">
              此场地当前可用
            </label>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default VenuesPage;